import React, { useMemo } from 'react';
import { graphql } from 'gatsby';
import {
    Layout,
    Head as DefaultHead,
} from 'gatsby-theme-soluble-source';

import SimpleHeader from 'design-system/src/components/organisms/SimpleHeader';
import { getHeaderAlert, getBreadcrumbLinks } from '../utils/page';
import BreadcrumbSection from 'design-system/src/components/organisms/BreadcrumbSection';

import Section from '../sections';

export function Head(props) {
    const structuredData = props?.data?.datoCmsPage?.structuredData;

    return <DefaultHead {...props} structuredData={structuredData} />;
}

export default function Page({ data, path, location }) {
    const isHome = data?.home?.id === data.datoCmsPage.id;
    const isAbm = data?.datoCmsPage?.abmKey;

    const [isLandingForm, showBlockDemoSectionOnlyMobile] = useMemo(() => {
        if (
            data?.datoCmsPage?.sections?.length &&
            data.datoCmsPage.sections[0].typename === 'DatoCmsBlockDemoSection'
        ) {
            return [
                true,
                data.datoCmsPage.sections[0].positionForm === 'bot-top',
            ];
        }

        return [false, false];
    }, [data]);

    const hideHeader = data.datoCmsPage?.sections?.some(
        (section) => section.typename === 'DatoCmsContactTourSection',
    );

    const hideFooter = data.datoCmsPage?.sections?.some(
        (section) => section.typename === 'DatoCmsContactTourSection',
    );

    return (
        <>
            {(isLandingForm || isAbm) && <SimpleHeader theme={data.datoCmsPage.theme} />}
            <Layout
                {...(!hideFooter && {
                    ...(!isLandingForm && {
                        'data-header-default-child-height': true,
                    }),
                    ...(isLandingForm && {
                        'data-header-simple-child-height': true,
                    }),
                })}
                data-theme={data.datoCmsPage.theme}
                hideHeader={hideHeader || isLandingForm || isAbm}
                hideFooter={hideFooter}
                headerProps={{
                    alert: getHeaderAlert(data.datoCmsPage),
                }}
                footerProps={{
                    showBlockDemoSection:
                        data?.datoCmsPage?.footerBlockDemoSection,
                    showBlockDemoSectionOnlyMobile,
                }}>
                {data.datoCmsPage.sections.map((section, i) => (
                    <Section
                        key={i}
                        order={i + 1}
                        path={path}
                        location={location}
                        isLast={i + 1 === data.datoCmsPage.sections.length}
                        previousSectionType={
                            i ? data.datoCmsPage.sections[i - 1].typename : null
                        }
                        nextSectionType={
                            data.datoCmsPage.sections.length > i + 1
                                ? data.datoCmsPage.sections[i + 1].typename
                                : null
                        }
                        {...section}
                    />
                ))}
                {!isHome &&
                    !hideFooter &&
                    !showBlockDemoSectionOnlyMobile && !isAbm ? (
                    <BreadcrumbSection
                        links={getBreadcrumbLinks(data.home, data.datoCmsPage)}
                    />
                ) : null}
            </Layout>
        </>
    );
}

export const query = graphql`
    fragment SourceMedia on DatoCmsFileField {
        alt
        title
        width
        height
        src: url
        format
    }
    fragment YoutubeVideo on DatoCmsVideoField {
        height
        width
        videoId: providerUid
        thumbnailUrl: thumbnailUrlMax
        title
    }
    fragment Button on DatoCmsButton {
        children: label
        href
        target
        label
    }
    fragment Form on DatoCmsForm {
        src: url
    }
    fragment Point on DatoCmsPoint {
        title
        description
        icon {
            ...SourceMedia
            gatsbyImageData(height: 128, placeholder: BLURRED)
        }
    }
    fragment BreadcrumbPart on DatoCmsPage {
        slug
        title
    }

    fragment OldButton on DatoCmsButton {
        label
        id
        detached
        href
        target
    }
    query Page($id: String, $language: String) {
        home: datoCmsPage(slug: { eq: "/" }, locale: { eq: $language }) {
            id
            ...BreadcrumbPart
        }
        datoCmsPage(id: { eq: $id }, locale: { eq: $language }) {
            id
            title
            theme
            structuredData
            headerAlert
            footerBlockDemoSection
            abmKey
            treeParent {
                ...BreadcrumbPart
                headerAlert
                treeParent {
                    ...BreadcrumbPart
                    headerAlert
                    treeParent {
                        ...BreadcrumbPart
                        headerAlert
                    }
                }
            }
            sections {
                typename: __typename
                ... on DatoCmsHeroComboSection {
                    ...DatoCmsHeroComboSection
                }
                ... on DatoCmsHeroHomeSection {
                    ...DatoCmsHeroHomeSection
                }
                ... on DatoCmsBlockTitleLargeSection {
                    ...DatoCmsBlockTitleLargeSection
                }
                ... on DatoCmsBlockTitleSmallSection {
                    ...DatoCmsBlockTitleSmallSection
                }
                ... on DatoCmsBandLogosSection {
                    ...DatoCmsBandLogosSection
                }
                ... on DatoCmsBlockContentLargeSection {
                    ...DatoCmsBlockContentLargeSection
                }
                ... on DatoCmsMediaFullSection {
                    ...DatoCmsMediaFullSection
                }
                ... on DatoCmsBlockDemoSection {
                    ...DatoCmsBlockDemoSection
                }
                ... on DatoCmsFormSection {
                    ...DatoCmsFormSection
                }
                ... on DatoCmsBannerNewsletterSection {
                    ...DatoCmsBannerNewsletterSection
                }
                ... on DatoCmsCalculatorSection {
                    ...DatoCmsCalculatorSection
                }
                ... on DatoCmsBannerLargeSection {
                    ...DatoCmsBannerLargeSection
                }
                ... on DatoCmsBannerSmallSection {
                    ...DatoCmsBannerSmallSection
                }
                ... on DatoCmsContactTourSection {
                    ...DatoCmsContactTourSection
                }
                ... on DatoCmsBlockWorldSection {
                    ...DatoCmsBlockWorldSection
                }
                ... on DatoCmsStorySection {
                    ...DatoCmsStorySection
                }
                ... on DatoCmsGridPricingSection {
                    ...DatoCmsGridPricingSection
                }
                ... on DatoCmsAwardsSection {
                    ...DatoCmsAwardsSection
                }
                ... on DatoCmsBlockTrustSection {
                    ...DatoCmsBlockTrustSection
                }
                ... on DatoCmsBlogSection {
                    ...DatoCmsBlogSection
                }
                ... on DatoCmsGallerySection {
                    ...DatoCmsGallerySection
                }
                ... on DatoCmsBlockColumnsSection {
                    ...DatoCmsBlockColumnsSection
                }
                ... on DatoCmsAccordion3Section {
                    ...DatoCmsAccordion3Section
                }
                ... on DatoCmsBlockListLargeSection {
                    ...DatoCmsBlockListLargeSection
                }
                ... on DatoCmsGridInfo1Section {
                    ...DatoCmsGridInfo1Section
                }
                ... on DatoCmsGridInfo2Section {
                    ...DatoCmsGridInfo2Section
                }
                ... on DatoCmsGridInfo3Section {
                    ...DatoCmsGridInfo3Section
                }
                ... on DatoCmsPricingDetailsTable {
                    ...DatoCmsPricingDetailsTable
                }
                ... on DatoCmsGlossarySection {
                    ...DatoCmsGlossarySection
                }
                ... on DatoCmsCarousel1Section {
                    ...DatoCmsCarousel1Section
                }
                ... on DatoCmsGridNavigation1Section {
                    ...DatoCmsGridNavigation1Section
                }
                ... on DatoCmsGridNavigation2Section {
                    ...DatoCmsGridNavigation2Section
                }
                ... on DatoCmsPressSection {
                    ...DatoCmsPressSection
                }
                ... on DatoCmsGridNavigation3Section {
                    ...DatoCmsGridNavigation3Section
                }
                ... on DatoCmsBlockFaqsSection {
                    ...DatoCmsBlockFaqsSection
                }
                ... on DatoCmsTableSection {
                    ...DatoCmsTableSection
                }
                ... on DatoCmsResourcesSection {
                    ...DatoCmsResourcesSection
                }
                ... on DatoCmsRichTextSection {
                    ...DatoCmsRichTextSection
                }
                ... on DatoCmsJoinCommunitySection {
                    ...DatoCmsJoinCommunitySection
                }
                ... on DatoCmsStoriesSection {
                    ...DatoCmsStoriesSection
                }
            }
        }
    }
`;
